import { Injectable, Injector } from '@angular/core';
// Type-only (see issues.service.spec.ts / CLAUDE.md "TypeScript Conventions"): the @newgenus/common
// barrel has a side-effectful every-moment import that throws under jest when this import is
// pulled in as a runtime value under ts-jest's isolatedModules.
import type { ModuleContext, User } from '@newgenus/common';
import { ModuleSwitchService } from '../shared/services/module-switch.service';
import { NotificationService } from '../shared/services/notification.service';
import { CentralSessionService } from './central-session.service';
import { IssuesFirebase } from './central-firebase';

/**
 * Eager central-session bootstrap (ISSUE-TRACKER-V2-DESIGN.md §3, "Eager central session").
 *
 * Today `CentralSessionService` only gets constructed once the lazy issues module loads, so a
 * user who never opens the tracker this session sees no issue notifications even though they're
 * entitled to them. This service, called once from the header next to
 * `notificationService.initialize(uid)`, makes the SAME judgement the sidebar menu uses
 * (`issue-tracker` module switch, tier-3 `issues` feature grant / developer / admin) and, only
 * when the tracker is actually on for this user, resolves `CentralSessionService` via the
 * `Injector` (NOT a constructor dependency — see below) to start the token exchange, then wires
 * it into the bell via `NotificationService.attachCentralStore()`.
 *
 * `Injector.get()` (not constructor injection) is deliberate: `CentralSessionService`'s own
 * constructor subscribes to `AngularFireAuth.authState` immediately on construction, which is
 * exactly the exchange we must NOT start for a user without the tracker. If this service took
 * `CentralSessionService` as a constructor param, merely injecting `IssueTrackerAccessService`
 * into the header (which happens for every user) would eagerly construct it for everyone.
 */
@Injectable({ providedIn: 'root' })
export class IssueTrackerAccessService {
    private granted = false;

    constructor(
        private injector: Injector,
        private moduleSwitch: ModuleSwitchService
    ) {}

    /** Safe to call on every user emission (e.g. every header ngOnInit user$ tick) — a no-op once
     *  access has already been granted this session, and a no-op forever for a user the tracker
     *  is dark for. */
    public async bootstrapIfAllowed(
        user: User | undefined | null,
        notificationService: NotificationService
    ): Promise<void> {
        if (this.granted || !user?.uid) return;

        await this.moduleSwitch.ready();
        if (!this.isTrackerOn(user)) return;

        this.granted = true;
        const centralSession = this.injector.get(CentralSessionService);
        const issuesFirebase = this.injector.get(IssuesFirebase);
        notificationService.attachCentralStore(centralSession, issuesFirebase);
    }

    /** Same judgement as the sidebar (security.service.ts `buildMenuFromUserAccess`):
     *  `issue-tracker` switch on for this org context (tier-3 `issues` feature grant), or a
     *  developer (who sees a dark module only where it allows developers — moduleSwitch.isOn
     *  already encodes that). */
    private isTrackerOn(user: User): boolean {
        const organizations = user.organizations as Record<string, { features?: string[] }> & { selected?: string };
        const selectedOrgKey = organizations?.selected;
        const selectedOrg = selectedOrgKey ? organizations?.[selectedOrgKey] : undefined;
        const featureArray = selectedOrg?.features ?? [];
        const ctx: ModuleContext = {
            orgId: selectedOrgKey,
            userFeatures: featureArray,
            isDeveloper: !!user.isDeveloper,
        };
        return this.moduleSwitch.isOn('issue-tracker', ctx);
    }
}
